import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { getBroadcastConfig, updateBroadcastConfig } from './firebase';

/* =====================================================================
   BROADCAST.JS  (v2)
   Antrian Broadcast Telegram + Library Template + Riwayat Pengiriman.

   Perubahan utama dibanding versi sebelumnya:
   1. RIWAYAT: tab khusus, menyimpan hingga 100 log lengkap (tanggal, jam,
      sumber Otomatis/Manual/Template, isi pesan, status, pesan error).
      Semua jenis pengiriman kini ikut tersimpan ke Firestore.
   2. SINKRON FIRESTORE: tombol Simpan menunggu hasil sebenarnya dari
      Firestore (berhasil/gagal), ada indikator "belum disimpan", bilah
      simpan melayang, Ctrl+S, dan peringatan sebelum menutup tab.
   3. ANTI-LAG: gambar dikompres otomatis sebelum disimpan, kartu
      di-memo (hanya kartu yang berubah yang dirender ulang), loop
      pengiriman tidak dibuat ulang tiap state berubah.
   4. TAMPILAN: desain ulang profesional dengan ikon SVG, ukuran teks
      yang terbaca, dialog konfirmasi, dan toast sukses/error.
   Seluruh logika lama (proteksi template terkunci, jadwal WIB, interval,
   auto-hapus setelah terkirim, preview) tetap dipertahankan.

   Penambahan terbaru:
   5. AUTO-OFF: begitu seluruh slot antrian berhasil terkirim (antrian habis),
      engine otomatis dinonaktifkan (OFF) dan tidak mengirim lagi sampai
      diaktifkan kembali. Selama masih ada slot tersisa, engine tetap berjalan
      normal. Template & pengaturan tidak pernah dihapus oleh proses ini.
   6. PASTE GAMBAR: tekan Ctrl+V (screenshot / salin gambar dari browser atau
      aplikasi lain) pada slot broadcast; gambar langsung tampil sebagai
      pratinjau dan bisa dihapus lewat tombol "Hapus".
   ===================================================================== */

// --- KONFIGURASI TELEGRAM BROADCAST ---
// Disarankan diisi lewat .env (REACT_APP_BROADCAST_BOT_TOKEN & REACT_APP_BROADCAST_CHAT_ID).
// Nilai di kanan "||" hanyalah cadangan agar perilaku sama persis dengan versi lama.
const BOT_TOKEN = process.env.REACT_APP_BROADCAST_BOT_TOKEN || "8017149378:AAFPJxzcQ5pMeCvq8P4N-9yOZ33Qr8hsjNM";
const CHAT_ID = process.env.REACT_APP_BROADCAST_CHAT_ID || "-1002936402906";

const CATEGORIES = ["SEMUA", "TEMPLATE HARIAN", "TELKOMSEL", "BYU", "THREE", "INDOSAT", "XL AXIS", "SMARTFREN"];

const INTERVAL_OPTIONS = [
    { label: "5 Menit", value: 300 },
    { label: "10 Menit", value: 600 },
    { label: "15 Menit", value: 900 },
    { label: "20 Menit", value: 1200 },
    { label: "30 Menit", value: 1800 },
    { label: "1 Jam", value: 3600 }
];

const MAX_LOGS = 100;              // jumlah riwayat yang disimpan
const MAX_LOG_TEXT = 800;          // panjang isi pesan yang disimpan di riwayat
const MAX_IMAGE_DIM = 1280;        // sisi terpanjang gambar setelah dikompres (px)
const IMAGE_QUALITY = 0.75;        // kualitas JPEG hasil kompres
const MAX_UPLOAD_MB = 15;          // batas ukuran file sebelum dikompres
const CAPTION_LIMIT = 1024;        // batas caption Telegram (pesan + foto)
const TEXT_LIMIT = 4096;           // batas pesan teks Telegram
const DOC_SOFT_LIMIT = 1000000;    // batas dokumen Firestore (~1 MB)

const SOURCE_LABEL = { AUTO: "Otomatis", MANUAL: "Manual", TEMPLATE: "Template" };

/* ------------------------------ Helper ------------------------------ */

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

const formatTime = (seconds) => {
    if (seconds <= 0) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const formatBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const estimateBytes = (messages, templates, logs) => {
    let n = 0;
    (messages || []).forEach(m => { n += (m.text || "").length + (m.fileData || "").length + 80; });
    (templates || []).forEach(t => { n += (t.text || "").length + (t.title || "").length + (t.fileData || "").length + 120; });
    (logs || []).forEach(l => { n += (l.fullText || "").length + (l.content || "").length + 180; });
    return n;
};

const normalizeMessages = (arr) =>
    (Array.isArray(arr) ? arr : []).map(m => ({
        ...m,
        id: m.id || uid(),
        text: m.text || "",
        fileData: m.fileData || null,
        scheduledWib: m.scheduledWib || ""
    }));

const normalizeTemplates = (arr) =>
    (Array.isArray(arr) ? arr : []).map(t => ({
        category: "TEMPLATE HARIAN",
        title: "",
        text: "",
        fileData: null,
        ...t
    }));

const normalizeLogs = (arr) =>
    (Array.isArray(arr) ? arr : []).map(l => ({ ...l, id: l.id || uid() }));

const buildLog = (msg, ok, source, error) => {
    const d = new Date();
    const text = (msg && msg.text) || "";
    return {
        id: uid(),
        ts: d.getTime(),
        time: d.toLocaleTimeString('id-ID'),
        date: d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }),
        content: text ? (text.length > 45 ? text.substring(0, 45) + "…" : text) : "Media saja",
        fullText: text.substring(0, MAX_LOG_TEXT),
        hasMedia: !!(msg && msg.fileData),
        source: source || "AUTO",
        status: ok ? "SUCCESS" : "FAILED",
        error: ok ? "" : (error || "Tidak diketahui")
    };
};

// Kompres gambar di browser agar dokumen Firestore tetap ringan & UI tidak lag
const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Gagal membaca file"));
    reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error("File bukan gambar yang valid"));
        img.onload = () => {
            const scale = Math.min(1, MAX_IMAGE_DIM / Math.max(img.width, img.height));
            const w = Math.max(1, Math.round(img.width * scale));
            const h = Math.max(1, Math.round(img.height * scale));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', IMAGE_QUALITY));
        };
        img.src = reader.result;
    };
    reader.readAsDataURL(file);
});

const dataUrlToBlob = (dataUrl) => {
    const base64Data = dataUrl.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: 'image/jpeg' });
};

// --- Fungsi Kirim Telegram (mengembalikan { ok, error }) ---
const sendToTelegram = async (msgObj) => {
    if (!msgObj || (!msgObj.text && !msgObj.fileData)) {
        return { ok: false, error: "Pesan kosong (tidak ada teks maupun gambar)" };
    }
    try {
        let response;
        if (msgObj.fileData) {
            const formData = new FormData();
            formData.append('chat_id', CHAT_ID);
            formData.append('photo', dataUrlToBlob(msgObj.fileData), 'image.jpg');
            formData.append('caption', msgObj.text || "");
            formData.append('parse_mode', 'HTML');
            response = await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendPhoto`, {
                method: 'POST',
                body: formData
            });
        } else {
            response = await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ chat_id: CHAT_ID, text: msgObj.text, parse_mode: "HTML" })
            });
        }
        if (response.ok) return { ok: true, error: "" };
        let desc = `HTTP ${response.status}`;
        try {
            const j = await response.json();
            if (j && j.description) desc = j.description;
        } catch (e) { /* abaikan */ }
        return { ok: false, error: desc };
    } catch (err) {
        console.error("Telegram Error:", err);
        return { ok: false, error: (err && err.message) || "Koneksi bermasalah" };
    }
};

/* ------------------------------- Ikon ------------------------------- */

const ICON_PATHS = {
    send: <><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>,
    eye: <><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></>,
    trash: <><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
    unlock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" /></>,
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
    save: <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" /><path d="M17 21v-8H7v8" /><path d="M7 3v5h8" /></>,
    refresh: <><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M8 16H3v5" /></>,
    clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
    image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" /></>,
    history: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l4 2" /></>,
    megaphone: <><path d="m3 11 18-5v12L3 14v-3Z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></>,
    file: <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2Z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h8" /></>,
    check: <path d="M20 6 9 17l-5-5" />,
    x: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
    alert: <><path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></>,
    upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m17 8-5-5-5 5" /><path d="M12 3v12" /></>,
    chevron: <path d="m6 9 6 6 6-6" />
};

const Icon = ({ name, className = "w-4 h-4" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
        {ICON_PATHS[name]}
    </svg>
);

const Spinner = ({ className = "w-4 h-4" }) => (
    <span className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`} />
);

/* ------------------------- Komponen Kecil -------------------------- */

const CharCounter = ({ len, limit }) => (
    <span className={`text-[11px] font-medium tabular-nums ${len > limit ? 'text-red-600' : 'text-slate-400'}`}>
        {len}/{limit}
    </span>
);

const SyncPill = ({ loading, saving, error, dirty, lastSavedAt }) => {
    let tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
    let dot = "bg-emerald-500";
    let label = lastSavedAt ? `Tersinkron • ${lastSavedAt.toLocaleTimeString('id-ID')}` : "Tersinkron dengan Firestore";
    if (loading) {
        tone = "bg-slate-50 text-slate-500 border-slate-200"; dot = "bg-slate-400"; label = "Memuat data…";
    } else if (saving) {
        tone = "bg-blue-50 text-blue-700 border-blue-200"; dot = "bg-blue-500"; label = "Menyimpan ke Firestore…";
    } else if (error) {
        tone = "bg-red-50 text-red-700 border-red-200"; dot = "bg-red-500"; label = "Gagal sinkron — coba simpan lagi";
    } else if (dirty) {
        tone = "bg-amber-50 text-amber-700 border-amber-200"; dot = "bg-amber-500"; label = "Ada perubahan belum disimpan";
    }
    return (
        <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${tone}`}>
            <span className={`h-2 w-2 rounded-full ${dot} ${saving || loading ? 'animate-pulse' : ''}`} />
            {label}
        </span>
    );
};

const IconButton = ({ icon, title, onClick, tone = "slate", disabled }) => {
    const tones = {
        slate: "text-slate-500 hover:bg-slate-100 hover:text-slate-800",
        blue: "text-blue-600 hover:bg-blue-50",
        green: "text-emerald-600 hover:bg-emerald-50",
        red: "text-red-500 hover:bg-red-50",
        amber: "text-amber-600 hover:bg-amber-50"
    };
    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            disabled={disabled}
            onClick={onClick}
            className={`flex h-8 w-8 items-center justify-center rounded-lg transition disabled:cursor-not-allowed disabled:opacity-40 ${tones[tone]}`}
        >
            <Icon name={icon} />
        </button>
    );
};

const Toast = ({ toast }) => {
    if (!toast) return null;
    const isError = toast.type === 'error';
    return (
        <div className="fixed top-5 left-1/2 z-[3000] w-[92%] -translate-x-1/2 md:w-auto">
            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-slate-900/95 px-5 py-3.5 text-white shadow-2xl backdrop-blur">
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${isError ? 'bg-red-500' : 'bg-emerald-500'}`}>
                    <Icon name={isError ? 'x' : 'check'} className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm font-medium">{toast.msg}</span>
            </div>
        </div>
    );
};

const ConfirmDialog = ({ state, onCancel, onConfirm }) => {
    if (!state) return null;
    return (
        <div className="fixed inset-0 z-[2500] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={onCancel}>
            <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-red-50 text-red-500">
                    <Icon name="alert" className="w-5 h-5" />
                </div>
                <h4 className="text-base font-semibold text-slate-900">{state.title}</h4>
                <p className="mt-1.5 text-sm text-slate-500">{state.message}</p>
                <div className="mt-6 flex gap-3">
                    <button onClick={onCancel} className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">Batal</button>
                    <button onClick={onConfirm} className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700">{state.confirmLabel || "Ya, lanjutkan"}</button>
                </div>
            </div>
        </div>
    );
};

const PreviewModal = ({ item, onClose }) => {
    if (!item) return null;
    return (
        <div className="fixed inset-0 z-[2000] flex items-end justify-center bg-slate-900/60 p-0 backdrop-blur-sm md:items-center md:p-4" onClick={onClose}>
            <div className="w-full max-w-sm overflow-hidden rounded-t-3xl bg-white shadow-2xl md:rounded-3xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-5 py-4">
                    <span className="text-sm font-semibold text-slate-700">Preview Pesan</span>
                    <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-red-500" aria-label="Tutup">
                        <Icon name="x" />
                    </button>
                </div>
                <div className="p-5">
                    {item.fileData && <img src={item.fileData} className="mb-4 max-h-56 w-full rounded-xl border border-slate-100 object-cover" alt="Preview" />}
                    <div className="max-h-60 overflow-y-auto rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                            {item.text || "Tanpa teks…"}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ------------------- Kartu Antrian Broadcast (memo) ------------------- */

const BroadcastCard = memo(({ msg, index, isNext, onChange, onImage, onPreview, onSend, onDelete, onHover }) => {
    const fileRef = useRef(null);
    const limit = msg.fileData ? CAPTION_LIMIT : TEXT_LIMIT;
    const len = (msg.text || "").length;

    return (
        <div
            data-slot-id={msg.id}
            onMouseEnter={() => onHover && onHover(msg.id)}
            onMouseLeave={() => onHover && onHover(null)}
            className={`flex flex-col rounded-2xl border bg-white p-4 shadow-sm transition ${isNext ? 'border-blue-500 ring-4 ring-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
        >
            <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-xs font-semibold text-white">{index + 1}</span>
                    <label className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-slate-500 focus-within:border-blue-400 focus-within:bg-white">
                        <Icon name="clock" className="w-3.5 h-3.5" />
                        <input
                            type="time"
                            value={msg.scheduledWib || ""}
                            onChange={(e) => onChange(msg.id, 'scheduledWib', e.target.value)}
                            className="bg-transparent text-xs font-medium text-slate-700 outline-none"
                            title="Jadwal kirim (WIB). Kosongkan untuk mengikuti interval."
                        />
                    </label>
                </div>
                <div className="flex gap-0.5">
                    <IconButton icon="eye" title="Preview" tone="blue" onClick={() => onPreview(msg)} />
                    <IconButton icon="send" title="Kirim sekarang" tone="green" onClick={() => onSend(msg.id)} />
                    <IconButton icon="trash" title="Hapus slot" tone="red" onClick={() => onDelete(msg.id)} />
                </div>
            </div>

            <div
                tabIndex={0}
                title="Klik area ini lalu tekan Ctrl+V untuk menempel gambar"
                className="relative h-36 overflow-hidden rounded-xl border border-dashed border-slate-300 bg-slate-50 outline-none focus:border-blue-400"
            >
                {msg.fileData ? (
                    <>
                        <img src={msg.fileData} className="h-full w-full object-cover" alt="Media" />
                        <div className="absolute right-2 top-2 flex gap-1">
                            <button type="button" onClick={() => fileRef.current && fileRef.current.click()} className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-700 shadow hover:bg-white">Ganti</button>
                            <button type="button" onClick={() => onChange(msg.id, 'fileData', null)} className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-red-600 shadow hover:bg-white">Hapus</button>
                        </div>
                    </>
                ) : (
                    <button type="button" onClick={() => fileRef.current && fileRef.current.click()} className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-slate-400 transition hover:bg-blue-50/50 hover:text-blue-600">
                        <Icon name="upload" className="w-5 h-5" />
                        <span className="text-xs font-semibold">Unggah gambar</span>
                        <span className="text-[11px]">Dikompres otomatis • atau tempel (Ctrl+V)</span>
                    </button>
                )}
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        e.target.value = "";
                        if (f) onImage(msg.id, f);
                    }}
                />
            </div>

            <div className="mt-3">
                <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">{msg.fileData ? "Caption" : "Isi pesan"}</span>
                    <CharCounter len={len} limit={limit} />
                </div>
                <textarea
                    value={msg.text || ""}
                    onChange={(e) => onChange(msg.id, 'text', e.target.value)}
                    className={`h-28 w-full resize-none rounded-xl border bg-slate-50/60 p-3 text-sm text-slate-700 outline-none transition focus:bg-white ${len > limit ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-blue-400'}`}
                    placeholder="Tulis pesan broadcast…"
                />
                {len > limit && <p className="mt-1 text-[11px] text-red-600">Melebihi batas Telegram, pengiriman akan gagal.</p>}
            </div>

            {isNext && (
                <div className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" /> Giliran berikutnya
                </div>
            )}
        </div>
    );
});

/* --------------------- Kartu Template (memo) --------------------- */

const TemplateCard = memo(({ tpl, locked, onChange, onImage, onToggleLock, onDelete, onPreview, onSend }) => {
    const fileRef = useRef(null);
    const limit = tpl.fileData ? CAPTION_LIMIT : TEXT_LIMIT;
    const len = (tpl.text || "").length;

    return (
        <div className={`flex flex-col justify-between rounded-2xl border p-4 transition ${locked ? 'border-slate-200 bg-slate-50' : 'border-slate-200 bg-white shadow-sm hover:border-indigo-300'}`}>
            <div>
                <div className="mb-3 flex items-center justify-between gap-2">
                    <select
                        disabled={locked}
                        value={tpl.category}
                        onChange={(e) => onChange(tpl.id, 'category', e.target.value)}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-600 outline-none focus:border-indigo-400 disabled:opacity-60"
                    >
                        {CATEGORIES.slice(1).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <div className="flex gap-0.5">
                        <IconButton icon={locked ? 'lock' : 'unlock'} title={locked ? "Buka kunci" : "Kunci template"} tone={locked ? 'amber' : 'slate'} onClick={() => onToggleLock(tpl.id)} />
                        {!locked && <IconButton icon="trash" title="Hapus template" tone="red" onClick={() => onDelete(tpl.id)} />}
                    </div>
                </div>

                <div className="relative mb-3 h-32 overflow-hidden rounded-xl border border-dashed border-slate-300 bg-slate-50">
                    {tpl.fileData ? (
                        <>
                            <img src={tpl.fileData} className="h-full w-full object-cover" alt="Template" />
                            {!locked && (
                                <div className="absolute right-2 top-2 flex gap-1">
                                    <button type="button" onClick={() => fileRef.current && fileRef.current.click()} className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-700 shadow hover:bg-white">Ganti</button>
                                    <button type="button" onClick={() => onChange(tpl.id, 'fileData', null)} className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-red-600 shadow hover:bg-white">Hapus</button>
                                </div>
                            )}
                        </>
                    ) : locked ? (
                        <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-slate-400">Tanpa gambar</div>
                    ) : (
                        <button type="button" onClick={() => fileRef.current && fileRef.current.click()} className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-slate-400 transition hover:bg-indigo-50/50 hover:text-indigo-600">
                            <Icon name="upload" className="w-5 h-5" />
                            <span className="text-xs font-semibold">Tambah gambar</span>
                        </button>
                    )}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                            const f = e.target.files && e.target.files[0];
                            e.target.value = "";
                            if (f) onImage(tpl.id, f);
                        }}
                    />
                </div>

                <input
                    disabled={locked}
                    type="text"
                    value={tpl.title || ""}
                    onChange={(e) => onChange(tpl.id, 'title', e.target.value)}
                    placeholder="Nama template"
                    className="mb-2 w-full bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:font-normal placeholder:text-slate-300 disabled:text-slate-500"
                />
                <div className="mb-1 flex justify-end"><CharCounter len={len} limit={limit} /></div>
                <textarea
                    disabled={locked}
                    value={tpl.text || ""}
                    onChange={(e) => onChange(tpl.id, 'text', e.target.value)}
                    className="h-28 w-full resize-none rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:bg-white disabled:opacity-60"
                    placeholder="Isi pesan broadcast…"
                />
            </div>

            <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
                <button onClick={() => onPreview(tpl)} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white py-2.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 active:scale-95">
                    <Icon name="eye" className="w-3.5 h-3.5" /> Preview
                </button>
                <button onClick={() => onSend(tpl)} className="flex flex-[1.4] items-center justify-center gap-1.5 rounded-xl bg-indigo-600 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 active:scale-95">
                    <Icon name="send" className="w-3.5 h-3.5" /> Kirim Sekarang
                </button>
            </div>
        </div>
    );
});

/* --------------------------- Tab Riwayat --------------------------- */

const HistoryTab = ({ logs, onClear }) => {
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [sourceFilter, setSourceFilter] = useState("ALL");
    const [query, setQuery] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const stats = useMemo(() => {
        const success = logs.filter(l => l.status === "SUCCESS").length;
        const failed = logs.length - success;
        const rate = logs.length ? Math.round((success / logs.length) * 100) : 0;
        return { total: logs.length, success, failed, rate };
    }, [logs]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return logs.filter(l => {
            if (statusFilter !== "ALL" && l.status !== statusFilter) return false;
            if (sourceFilter !== "ALL" && l.source !== sourceFilter) return false;
            if (q) {
                const hay = `${l.fullText || ""} ${l.content || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [logs, statusFilter, sourceFilter, query]);

    const chip = (active) => `rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`;

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                    { label: "Total Pengiriman", value: stats.total, tone: "text-slate-900" },
                    { label: "Berhasil", value: stats.success, tone: "text-emerald-600" },
                    { label: "Gagal", value: stats.failed, tone: "text-red-600" },
                    { label: "Tingkat Sukses", value: `${stats.rate}%`, tone: "text-blue-600" }
                ].map(s => (
                    <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <p className="text-xs font-medium text-slate-500">{s.label}</p>
                        <p className={`mt-1 text-2xl font-semibold tabular-nums ${s.tone}`}>{s.value}</p>
                    </div>
                ))}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="relative w-full lg:max-w-xs">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Icon name="search" /></span>
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Cari isi pesan…"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-blue-400 focus:bg-white"
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {[["ALL", "Semua"], ["SUCCESS", "Berhasil"], ["FAILED", "Gagal"]].map(([v, l]) => (
                            <button key={v} onClick={() => setStatusFilter(v)} className={chip(statusFilter === v)}>{l}</button>
                        ))}
                        <span className="mx-1 hidden h-5 w-px bg-slate-200 lg:block" />
                        {[["ALL", "Semua sumber"], ["AUTO", "Otomatis"], ["MANUAL", "Manual"], ["TEMPLATE", "Template"]].map(([v, l]) => (
                            <button key={v} onClick={() => setSourceFilter(v)} className={chip(sourceFilter === v)}>{l}</button>
                        ))}
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
                        <p className="text-sm font-semibold text-slate-600">{logs.length === 0 ? "Belum ada riwayat pengiriman" : "Tidak ada riwayat yang cocok"}</p>
                        <p className="mt-1 text-xs text-slate-400">{logs.length === 0 ? "Riwayat akan muncul otomatis setelah pesan dikirim." : "Coba ubah filter atau kata kunci pencarian."}</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map((log) => {
                            const ok = log.status === "SUCCESS";
                            const open = expandedId === log.id;
                            return (
                                <div key={log.id} className={`overflow-hidden rounded-xl border transition ${open ? 'border-slate-300 bg-slate-50/60' : 'border-slate-100 bg-white hover:border-slate-200'}`}>
                                    <button type="button" onClick={() => setExpandedId(open ? null : log.id)} className="flex w-full items-center gap-3 p-3 text-left">
                                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${ok ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                                            <Icon name={ok ? 'check' : 'x'} />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-medium text-slate-800">{log.content}</span>
                                            <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-400">
                                                <span className="tabular-nums">{log.date ? `${log.date} • ${log.time}` : log.time}</span>
                                                {log.source && SOURCE_LABEL[log.source] && (
                                                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-500">{SOURCE_LABEL[log.source]}</span>
                                                )}
                                                {log.hasMedia && <span className="inline-flex items-center gap-1"><Icon name="image" className="w-3 h-3" /> Gambar</span>}
                                            </span>
                                        </span>
                                        <span className={`hidden rounded-md px-2 py-1 text-[11px] font-semibold sm:inline ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                            {ok ? "Berhasil" : "Gagal"}
                                        </span>
                                        <span className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}><Icon name="chevron" /></span>
                                    </button>
                                    {open && (
                                        <div className="space-y-3 border-t border-slate-200 px-4 py-3">
                                            <div>
                                                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Isi pesan</p>
                                                <p className="whitespace-pre-wrap rounded-lg border border-slate-100 bg-white p-3 text-sm leading-relaxed text-slate-700">
                                                    {log.fullText || log.content}
                                                </p>
                                            </div>
                                            {!ok && log.error && (
                                                <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                                                    <Icon name="alert" className="mt-0.5 w-4 h-4 shrink-0" />
                                                    <span><span className="font-semibold">Penyebab gagal:</span> {log.error}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-slate-400">Menampilkan {filtered.length} dari {logs.length} riwayat • maksimal {MAX_LOGS} entri terbaru disimpan di Firestore.</p>
                    {logs.length > 0 && (
                        <button onClick={onClear} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-200 px-3.5 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50">
                            <Icon name="trash" className="w-3.5 h-3.5" /> Hapus Riwayat
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ============================ KOMPONEN UTAMA ============================ */

const Broadcast = () => {
    // --- State Navigasi ---
    const [activeTab, setActiveTab] = useState("broadcast");
    const [activeCategory, setActiveCategory] = useState("SEMUA");

    // --- State Broadcast & Templates ---
    const [broadcastMessages, setBroadcastMessages] = useState([]);
    const [isBcActive, setIsBcActive] = useState(false);
    const [nextSendIndex, setNextSendIndex] = useState(0);
    const [broadcastInterval, setBroadcastInterval] = useState(3600);
    const [countdown, setCountdown] = useState(0);
    const [isLoadingBc, setIsLoadingBc] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [lockedTemplates, setLockedTemplates] = useState({});
    const [nextRunTimestamp, setNextRunTimestamp] = useState(null);
    const [lastSentTimestamp, setLastSentTimestamp] = useState(null);

    // --- State Sinkronisasi ---
    const [isDirty, setIsDirty] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);

    // --- State Log, Preview, Toast, Konfirmasi ---
    const [broadcastLogs, setBroadcastLogs] = useState([]);
    const [previewItem, setPreviewItem] = useState(null);
    const [toast, setToast] = useState(null);
    const [confirmState, setConfirmState] = useState(null);

    const toastTimer = useRef(null);
    const sendingRef = useRef(false);
    const savingRef = useRef(false);
    const lastFiredRef = useRef("");
    const editVersionRef = useRef(0);
    const hoveredSlotRef = useRef(null);

    const triggerToast = useCallback((msg, type = 'success') => {
        setToast({ msg, type });
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), type === 'error' ? 3500 : 2200);
    }, []);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const markDirty = useCallback(() => {
        editVersionRef.current += 1;
        setIsDirty(true);
    }, []);

    const askConfirm = useCallback((cfg) => setConfirmState(cfg), []);

    // --- Efek Sinkronisasi Awal ---
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const config = await getBroadcastConfig();
                if (!alive) return;
                if (config) {
                    setBroadcastMessages(normalizeMessages(config.messages));
                    setIsBcActive(config.isActive || false);
                    setNextSendIndex(config.nextIndex || 0);
                    setLastSentTimestamp(config.lastSent || null);
                    setBroadcastInterval(config.interval || 3600);
                    setTemplates(normalizeTemplates(config.templates));
                    setNextRunTimestamp(config.nextRunTimestamp || null);
                    setBroadcastLogs(normalizeLogs(config.logs));
                    setLockedTemplates(config.lockedTemplates || {});
                }
            } catch (err) {
                console.error("Gagal memuat konfigurasi broadcast:", err);
                if (alive) triggerToast("Gagal memuat data dari Firestore", 'error');
            } finally {
                if (alive) setIsLoadingBc(false);
            }
        })();
        return () => { alive = false; };
    }, [triggerToast]);

    // --- Ref state terbaru (mencegah stale closure saat menyimpan ke Firebase) ---
    const latestRef = useRef({});
    latestRef.current = {
        messages: broadcastMessages,
        isActive: isBcActive,
        nextIndex: nextSendIndex,
        lastSent: lastSentTimestamp,
        interval: broadcastInterval,
        templates: templates,
        nextRunTimestamp: nextRunTimestamp,
        logs: broadcastLogs,
        lockedTemplates: lockedTemplates
    };

    const syncToCloud = useCallback(async (updates = {}) => {
        const versionAtStart = editVersionRef.current;
        // Selalu pakai state paling baru (bukan closure lama dari interval/effect)
        const cur = latestRef.current;
        const pick = (k) => (updates[k] !== undefined ? updates[k] : cur[k]);
        const payload = {
            messages: pick('messages'),
            isActive: pick('isActive'),
            nextIndex: pick('nextIndex'),
            lastSent: pick('lastSent'),
            interval: pick('interval'),
            templates: pick('templates'),
            nextRunTimestamp: pick('nextRunTimestamp'),
            logs: pick('logs'),
            lockedTemplates: pick('lockedTemplates')
        };

        // --- PROTEKSI TEMPLATE TERKUNCI ---
        // Template yang sudah terkunci di Firebase tidak boleh hilang/tertimpa oleh proses otomatis.
        // Hanya pengguna yang membuka kunci secara manual (flag = false) yang bisa melepasnya.
        try {
            const cloud = await getBroadcastConfig();
            if (cloud) {
                const cloudLocks = cloud.lockedTemplates || {};
                const cloudTpls = Array.isArray(cloud.templates) ? cloud.templates : [];
                const mergedLocks = { ...(payload.lockedTemplates || {}) };
                const mergedTpls = Array.isArray(payload.templates) ? [...payload.templates] : [];
                let restored = false;

                Object.keys(cloudLocks).forEach((id) => {
                    if (cloudLocks[id] !== true) return;
                    if (mergedLocks[id] === false) return; // dibuka kuncinya secara manual oleh pengguna
                    if (mergedLocks[id] !== true) {
                        mergedLocks[id] = true;
                        restored = true;
                    }
                });

                Object.keys(mergedLocks).forEach((id) => {
                    if (mergedLocks[id] !== true) return;
                    const exists = mergedTpls.some(t => String(t.id) === String(id));
                    if (!exists) {
                        const fromCloud = cloudTpls.find(t => String(t.id) === String(id));
                        if (fromCloud) {
                            mergedTpls.push(fromCloud);
                            restored = true;
                        }
                    }
                });

                if (restored) {
                    payload.lockedTemplates = mergedLocks;
                    payload.templates = mergedTpls;
                    setLockedTemplates(mergedLocks);
                    setTemplates(normalizeTemplates(mergedTpls));
                }
            }
        } catch (err) {
            console.error("Proteksi Template Terkunci Error:", err);
        }

        try {
            await updateBroadcastConfig(payload);
            if (updates.nextRunTimestamp !== undefined) setNextRunTimestamp(updates.nextRunTimestamp);
            setLastSavedAt(new Date());
            setSaveError(false);
            // Hanya dianggap "tersimpan" bila tidak ada edit baru selama proses simpan
            if (editVersionRef.current === versionAtStart) setIsDirty(false);
            return true;
        } catch (err) {
            console.error("Gagal sinkron ke Firestore:", err);
            setSaveError(true);
            return false;
        }
    }, []);

    // Simpan semua perubahan (broadcast + template + riwayat) ke Firestore
    const handleSaveAll = useCallback(async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setIsSaving(true);
        const ok = await syncToCloud({});
        savingRef.current = false;
        setIsSaving(false);
        triggerToast(ok ? "Tersimpan ke Firestore" : "Gagal menyimpan ke Firestore, coba lagi", ok ? 'success' : 'error');
    }, [syncToCloud, triggerToast]);

    // Ctrl/Cmd + S
    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                handleSaveAll();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [handleSaveAll]);

    // Peringatan bila menutup tab saat masih ada perubahan belum disimpan
    useEffect(() => {
        if (!isDirty) return undefined;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty]);

    // --- Riwayat ---
    const appendLog = useCallback((msg, ok, source, error) => {
        const entry = buildLog(msg, ok, source, error);
        const updated = [entry, ...(latestRef.current.logs || [])].slice(0, MAX_LOGS);
        latestRef.current.logs = updated; // langsung tersedia untuk syncToCloud berikutnya
        setBroadcastLogs(updated);
        return updated;
    }, []);

    // --- Countdown awal / saat timestamp berubah ---
    useEffect(() => {
        if (nextRunTimestamp) {
            const diff = Math.round((nextRunTimestamp - Date.now()) / 1000);
            setCountdown(diff > 0 ? diff : 0);
        } else {
            setCountdown(0);
        }
    }, [nextRunTimestamp]);

    // --- Logika Loop Broadcast ---
    // Loop hanya dibuat ulang saat engine dinyalakan/dimatikan; state terbaru dibaca dari latestRef.
    useEffect(() => {
        if (!isBcActive) return undefined;

        const tick = async () => {
            const cur = latestRef.current;
            const msgs = cur.messages || [];
            if (msgs.length === 0 || sendingRef.current) return;

            const now = new Date();
            const nowMs = now.getTime();
            const safeIdx = cur.nextIndex >= msgs.length ? 0 : cur.nextIndex;
            const currentMsg = msgs[safeIdx];
            if (!currentMsg) return;

            const wibTime = currentMsg.scheduledWib;
            let shouldSend = false;

            if (wibTime) {
                const [h, m] = wibTime.split(':');
                const fireKey = `${currentMsg.id}|${now.toDateString()}|${wibTime}`;
                if (
                    now.getHours() === parseInt(h, 10) &&
                    now.getMinutes() === parseInt(m, 10) &&
                    lastFiredRef.current !== fireKey
                ) {
                    shouldSend = true;
                    lastFiredRef.current = fireKey; // cegah kirim ganda dalam menit yang sama
                }
            } else if (cur.nextRunTimestamp) {
                const diff = Math.round((cur.nextRunTimestamp - nowMs) / 1000);
                setCountdown(diff > 0 ? diff : 0);
                if (diff <= 0) shouldSend = true;
            }

            if (!shouldSend) return;

            sendingRef.current = true;
            setIsSending(true);
            try {
                const result = await sendToTelegram(currentMsg);
                const updatedLogs = appendLog(currentMsg, result.ok, "AUTO", result.error);

                // Pakai daftar pesan terbaru (pengguna bisa saja mengedit selama proses kirim)
                const updatedMessages = [...latestRef.current.messages];
                if (result.ok) {
                    const pos = updatedMessages.findIndex(m => m.id === currentMsg.id);
                    if (pos !== -1) updatedMessages.splice(pos, 1);
                }

                const nextIdx = updatedMessages.length > 0 ? (safeIdx % updatedMessages.length) : 0;
                const nowStr = new Date().toLocaleTimeString('id-ID');
                const nextRun = Date.now() + (latestRef.current.interval * 1000);

                // Seluruh slot sudah terkirim -> engine otomatis OFF (tidak kirim lagi sampai diaktifkan kembali)
                const exhausted = result.ok && updatedMessages.length === 0;

                setBroadcastMessages(updatedMessages);
                setNextSendIndex(nextIdx);
                setLastSentTimestamp(nowStr);
                if (exhausted) {
                    setIsBcActive(false);
                    setNextRunTimestamp(null);
                    triggerToast("Semua slot terkirim — broadcast dinonaktifkan otomatis");
                }

                await syncToCloud({
                    messages: updatedMessages,
                    nextIndex: nextIdx,
                    lastSent: nowStr,
                    nextRunTimestamp: updatedMessages.length > 0 ? nextRun : null,
                    logs: updatedLogs,
                    ...(exhausted ? { isActive: false } : {})
                });
            } finally {
                sendingRef.current = false;
                setIsSending(false);
            }
        };

        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [isBcActive, appendLog, syncToCloud, triggerToast]);

    // --- Handler Broadcast ---
    const handleMessageChange = useCallback((id, field, value) => {
        setBroadcastMessages(prev => prev.map(m => (m.id === id ? { ...m, [field]: value } : m)));
        markDirty();
    }, [markDirty]);

    const handleMessageImage = useCallback(async (id, file, fromPaste) => {
        if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
            triggerToast(`Ukuran gambar maksimal ${MAX_UPLOAD_MB} MB`, 'error');
            return;
        }
        try {
            const dataUrl = await compressImage(file);
            handleMessageChange(id, 'fileData', dataUrl);
            if (fromPaste) triggerToast("Gambar berhasil ditempel");
        } catch (err) {
            triggerToast(err.message || "Gagal memproses gambar", 'error');
        }
    }, [handleMessageChange, triggerToast]);

    const handleHoverSlot = useCallback((id) => { hoveredSlotRef.current = id; }, []);

    // Paste gambar langsung (Ctrl+V) ke slot broadcast, seperti di Telegram.
    // Slot target: slot yang sedang difokuskan (textarea / area gambar), atau slot yang sedang di-hover.
    // Paste teks biasa tidak terganggu (hanya paste yang berisi gambar & tanpa teks yang ditangani).
    useEffect(() => {
        if (activeTab !== "broadcast") return undefined;
        const onPaste = (e) => {
            const cd = e.clipboardData;
            if (!cd) return;

            let file = null;
            const items = cd.items ? Array.from(cd.items) : [];
            for (const it of items) {
                if (it.kind === 'file' && it.type && it.type.indexOf('image/') === 0) {
                    file = it.getAsFile();
                    if (file) break;
                }
            }
            if (!file && cd.files && cd.files.length) {
                file = Array.from(cd.files).find(f => f.type && f.type.indexOf('image/') === 0) || null;
            }
            if (!file) return;
            if ((cd.getData('text/plain') || "").trim()) return; // ada teks -> biarkan paste teks normal

            const holder = e.target && e.target.closest ? e.target.closest('[data-slot-id]') : null;
            const slotId = holder ? holder.getAttribute('data-slot-id') : hoveredSlotRef.current;
            if (slotId === null || slotId === undefined || slotId === "") return;

            const target = (latestRef.current.messages || []).find(m => String(m.id) === String(slotId));
            if (!target) return;

            e.preventDefault();
            handleMessageImage(target.id, file, true);
        };
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
    }, [activeTab, handleMessageImage]);

    const handleAddSlot = useCallback(() => {
        setBroadcastMessages(prev => [...prev, { id: uid(), text: "", fileData: null, scheduledWib: "" }]);
        markDirty();
        triggerToast("Slot antrian ditambahkan");
    }, [markDirty, triggerToast]);

    const handleDeleteMessage = useCallback((id) => {
        const updated = latestRef.current.messages.filter(m => m.id !== id);
        setBroadcastMessages(updated);
        syncToCloud({ messages: updated });
        triggerToast("Slot dihapus");
    }, [syncToCloud, triggerToast]);

    const handleInstantSend = useCallback(async (id) => {
        const msg = latestRef.current.messages.find(m => m.id === id);
        if (!msg) return;
        if (sendingRef.current) {
            triggerToast("Sedang ada proses kirim, coba sesaat lagi", 'error');
            return;
        }
        sendingRef.current = true;
        setIsSending(true);
        try {
            const result = await sendToTelegram(msg);
            appendLog(msg, result.ok, "MANUAL", result.error);
            if (result.ok) {
                const updated = latestRef.current.messages.filter(m => m.id !== id);
                // Slot terakhir terkirim saat engine aktif -> engine otomatis OFF
                const exhausted = updated.length === 0 && latestRef.current.isActive;
                setBroadcastMessages(updated);
                if (exhausted) {
                    setIsBcActive(false);
                    setNextRunTimestamp(null);
                }
                triggerToast(exhausted ? "Terkirim • semua slot habis, broadcast dinonaktifkan" : "Terkirim & slot dihapus");
                await syncToCloud(exhausted
                    ? { messages: updated, isActive: false, nextRunTimestamp: null }
                    : { messages: updated });
            } else {
                triggerToast(`Gagal mengirim: ${result.error}`, 'error');
                await syncToCloud({});
            }
        } finally {
            sendingRef.current = false;
            setIsSending(false);
        }
    }, [appendLog, syncToCloud, triggerToast]);

    const handleStartBroadcast = useCallback((active) => {
        const nextRun = active ? Date.now() + (latestRef.current.interval * 1000) : null;
        setIsBcActive(active);
        setNextRunTimestamp(nextRun);
        syncToCloud({ isActive: active, nextRunTimestamp: nextRun });
    }, [syncToCloud]);

    const handleSelectInterval = useCallback((value) => {
        const nextRun = latestRef.current.isActive ? Date.now() + (value * 1000) : null;
        setBroadcastInterval(value);
        setNextRunTimestamp(nextRun);
        syncToCloud({ interval: value, nextRunTimestamp: nextRun });
    }, [syncToCloud]);

    const resetBroadcastQueue = useCallback(() => {
        const interval = latestRef.current.interval;
        const nextRun = Date.now() + (interval * 1000);
        setNextSendIndex(0);
        setCountdown(interval);
        if (latestRef.current.isActive) setNextRunTimestamp(nextRun);
        syncToCloud({ nextIndex: 0, nextRunTimestamp: latestRef.current.isActive ? nextRun : null });
        triggerToast("Antrian di-reset");
    }, [syncToCloud, triggerToast]);

    const handleDeleteAllMessages = useCallback(() => {
        askConfirm({
            title: "Hapus semua slot antrian?",
            message: "Seluruh pesan di antrian broadcast akan dihapus permanen dari Firestore.",
            confirmLabel: "Hapus semua",
            onConfirm: () => {
                setBroadcastMessages([]);
                setNextSendIndex(0);
                syncToCloud({ messages: [], nextIndex: 0 });
                triggerToast("Semua slot dihapus");
            }
        });
    }, [askConfirm, syncToCloud, triggerToast]);

    const handleClearLogs = useCallback(() => {
        askConfirm({
            title: "Hapus seluruh riwayat?",
            message: "Riwayat pengiriman akan dihapus permanen dari Firestore dan tidak bisa dikembalikan.",
            confirmLabel: "Hapus riwayat",
            onConfirm: () => {
                latestRef.current.logs = [];
                setBroadcastLogs([]);
                syncToCloud({ logs: [] });
                triggerToast("Riwayat dihapus");
            }
        });
    }, [askConfirm, syncToCloud, triggerToast]);

    // --- Handler Template ---
    const handleTemplateChange = useCallback((id, field, value) => {
        if (latestRef.current.lockedTemplates[id]) return;
        setTemplates(prev => prev.map(t => (t.id === id ? { ...t, [field]: value } : t)));
        markDirty();
    }, [markDirty]);

    const handleTemplateImage = useCallback(async (id, file) => {
        if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
            triggerToast(`Ukuran gambar maksimal ${MAX_UPLOAD_MB} MB`, 'error');
            return;
        }
        try {
            const dataUrl = await compressImage(file);
            handleTemplateChange(id, 'fileData', dataUrl);
        } catch (err) {
            triggerToast(err.message || "Gagal memproses gambar", 'error');
        }
    }, [handleTemplateChange, triggerToast]);

    const handleCreateTemplate = useCallback(() => {
        setTemplates(prev => [{ id: Date.now(), category: "TEMPLATE HARIAN", title: "", text: "", fileData: null }, ...prev]);
        markDirty();
    }, [markDirty]);

    const toggleLock = useCallback((id) => {
        const cur = latestRef.current;
        const newLocks = { ...cur.lockedTemplates, [id]: !cur.lockedTemplates[id] };
        setLockedTemplates(newLocks);
        // Saat dikunci, isi template ikut disimpan permanen ke Firebase bersama flag kuncinya
        syncToCloud({ lockedTemplates: newLocks, templates: cur.templates });
        triggerToast(newLocks[id] ? "Template dikunci & tersimpan" : "Kunci template dibuka");
    }, [syncToCloud, triggerToast]);

    const handleDeleteTemplate = useCallback((id) => {
        askConfirm({
            title: "Hapus template ini?",
            message: "Template akan dihapus dari library. Klik Simpan agar perubahan tersinkron ke Firestore.",
            confirmLabel: "Hapus",
            onConfirm: () => {
                setTemplates(prev => prev.filter(t => t.id !== id));
                markDirty();
            }
        });
    }, [askConfirm, markDirty]);

    const handleSendTemplate = useCallback(async (tpl) => {
        if (sendingRef.current) {
            triggerToast("Sedang ada proses kirim, coba sesaat lagi", 'error');
            return;
        }
        sendingRef.current = true;
        setIsSending(true);
        try {
            const result = await sendToTelegram(tpl);
            appendLog({ text: tpl.text || tpl.title || "", fileData: tpl.fileData }, result.ok, "TEMPLATE", result.error);
            triggerToast(result.ok ? "Template terkirim" : `Gagal mengirim: ${result.error}`, result.ok ? 'success' : 'error');
            await syncToCloud({});
        } finally {
            sendingRef.current = false;
            setIsSending(false);
        }
    }, [appendLog, syncToCloud, triggerToast]);

    // --- Data turunan ---
    const filteredTemplates = useMemo(
        () => templates.filter(t => activeCategory === "SEMUA" || t.category === activeCategory),
        [templates, activeCategory]
    );

    const docBytes = useMemo(
        () => estimateBytes(broadcastMessages, templates, broadcastLogs),
        [broadcastMessages, templates, broadcastLogs]
    );
    const docPercent = Math.min(100, Math.round((docBytes / DOC_SOFT_LIMIT) * 100));

    const safeNextIdx = nextSendIndex >= broadcastMessages.length ? 0 : nextSendIndex;
    const nextMsg = broadcastMessages[safeNextIdx];
    const statusText = isLoadingBc
        ? "Memuat…"
        : isBcActive
            ? (broadcastMessages.length === 0 ? "Aktif • antrian kosong" : `Berikutnya ${(nextMsg && nextMsg.scheduledWib) || formatTime(countdown)}`)
            : "Offline";

    const tabs = [
        { id: "broadcast", label: "Broadcast", icon: "megaphone", count: broadcastMessages.length },
        { id: "template", label: "Templates", icon: "file", count: templates.length },
        { id: "history", label: "Riwayat", icon: "history", count: broadcastLogs.length }
    ];

    const btnBase = "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60";

    return (
        <div className="relative mx-auto max-w-7xl space-y-6 rounded-3xl bg-slate-50 p-4 pb-28 md:p-8 md:pb-28">

            <Toast toast={toast} />
            <PreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
            <ConfirmDialog
                state={confirmState}
                onCancel={() => setConfirmState(null)}
                onConfirm={() => {
                    const fn = confirmState && confirmState.onConfirm;
                    setConfirmState(null);
                    if (fn) fn();
                }}
            />

            {/* Header Utama */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-200">
                        <Icon name="megaphone" className="w-6 h-6" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">Web Admin IFYOne</h2>
                        <p className="mt-0.5 flex items-center gap-2 text-xs font-medium text-slate-500">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                            Sistem Broadcast &amp; Template
                        </p>
                    </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <SyncPill loading={isLoadingBc} saving={isSaving} error={saveError} dirty={isDirty} lastSavedAt={lastSavedAt} />
                    <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm no-scrollbar">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition ${activeTab === tab.id ? 'bg-blue-600 text-white shadow' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
                            >
                                <Icon name={tab.icon} />
                                {tab.label}
                                <span className={`rounded-md px-1.5 py-0.5 text-[11px] tabular-nums ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* TAB: BROADCAST */}
            {activeTab === "broadcast" && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">

                        {/* Status Card */}
                        <div className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex items-center gap-4">
                                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${isBcActive ? 'bg-emerald-50 text-emerald-600 ring-4 ring-emerald-50' : 'bg-slate-100 text-slate-400'}`}>
                                    {isSending ? <Spinner className="w-5 h-5" /> : <Icon name="send" className="w-5 h-5" />}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-medium text-slate-500">Status Broadcast</p>
                                    <p className={`truncate text-lg font-semibold tabular-nums ${isBcActive ? 'text-slate-900' : 'text-slate-400'}`}>
                                        {isSending ? "Mengirim…" : statusText}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                                <span className="text-sm font-medium text-slate-600">Aktifkan Engine</span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={isBcActive}
                                    disabled={isLoadingBc}
                                    onClick={() => handleStartBroadcast(!isBcActive)}
                                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition disabled:opacity-50 ${isBcActive ? 'bg-blue-600' : 'bg-slate-300'}`}
                                >
                                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${isBcActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>
                            {lastSentTimestamp && (
                                <p className="mt-3 text-xs text-slate-400">Terakhir kirim: <span className="font-semibold text-slate-600">{lastSentTimestamp}</span></p>
                            )}
                        </div>

                        {/* Interval Settings Card */}
                        <div className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:col-span-2">
                            <div>
                                <p className="mb-3 text-xs font-medium text-slate-500">Interval pengiriman antar pesan</p>
                                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                                    {INTERVAL_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            onClick={() => handleSelectInterval(opt.value)}
                                            className={`rounded-xl border py-2.5 text-xs font-semibold transition ${broadcastInterval === opt.value ? 'border-blue-600 bg-blue-600 text-white shadow' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'}`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-4 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
                                <span>Slot dengan jam jadwal (WIB) dikirim sesuai jamnya, mengabaikan interval.</span>
                                <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600">
                                    <Icon name="check" className="w-3.5 h-3.5" /> Hapus otomatis setelah terkirim
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h3 className="text-base font-semibold text-slate-900">Antrian Pesan</h3>
                            <p className="text-xs text-slate-500">{broadcastMessages.length} slot • Ctrl+S untuk menyimpan cepat</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                            <button onClick={handleAddSlot} className={`${btnBase} bg-slate-900 text-white hover:bg-slate-800`}>
                                <Icon name="plus" /> Tambah Slot
                            </button>
                            <button onClick={handleSaveAll} disabled={isSaving} className={`${btnBase} ${isSaving ? 'bg-blue-500' : 'bg-blue-600 hover:bg-blue-700'} text-white`}>
                                {isSaving ? <Spinner /> : <Icon name="save" />} {isSaving ? "Menyimpan…" : "Simpan Broadcast"}
                            </button>
                            <button onClick={resetBroadcastQueue} className={`${btnBase} border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100`}>
                                <Icon name="refresh" /> Reset Antrian
                            </button>
                            <button onClick={handleDeleteAllMessages} className={`${btnBase} border border-red-200 bg-red-50 text-red-600 hover:bg-red-100`}>
                                <Icon name="trash" /> Hapus Semua
                            </button>
                        </div>
                    </div>

                    {/* List Cards Antrian Broadcast */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
                        {broadcastMessages.map((msg, index) => (
                            <BroadcastCard
                                key={msg.id}
                                msg={msg}
                                index={index}
                                isNext={isBcActive && safeNextIdx === index}
                                onChange={handleMessageChange}
                                onImage={handleMessageImage}
                                onPreview={setPreviewItem}
                                onSend={handleInstantSend}
                                onDelete={handleDeleteMessage}
                                onHover={handleHoverSlot}
                            />
                        ))}
                    </div>

                    {!isLoadingBc && broadcastMessages.length === 0 && (
                        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/70 p-10 text-center">
                            <p className="text-sm font-semibold text-slate-600">Antrian broadcast kosong</p>
                            <p className="mt-1 text-xs text-slate-400">Klik “Tambah Slot” untuk membuat pesan baru.</p>
                        </div>
                    )}

                    {/* Aktivitas Terakhir */}
                    {broadcastLogs.length > 0 && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="mb-3 flex items-center justify-between">
                                <p className="text-sm font-semibold text-slate-900">Aktivitas Terakhir</p>
                                <button onClick={() => setActiveTab("history")} className="text-xs font-semibold text-blue-600 hover:underline">Lihat semua riwayat →</button>
                            </div>
                            <div className="space-y-2">
                                {broadcastLogs.slice(0, 5).map((log) => (
                                    <div key={log.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                                        <div className="flex min-w-0 items-center gap-3">
                                            <span className="shrink-0 font-mono text-xs font-semibold text-slate-500">{log.time}</span>
                                            <span className="truncate text-sm text-slate-600">{log.content}</span>
                                        </div>
                                        <span className={`shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold ${log.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                            {log.status === 'SUCCESS' ? "Berhasil" : "Gagal"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB: TEMPLATES */}
            {activeTab === "template" && (
                <div className="space-y-6">
                    {/* Category Selector Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                        {CATEGORIES.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                className={`whitespace-nowrap rounded-xl border px-4 py-2 text-xs font-semibold transition ${activeCategory === cat ? 'border-indigo-600 bg-indigo-600 text-white shadow' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-8">
                        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900">Library Template</h3>
                                <p className="text-xs text-slate-500">Siap kirim ke Telegram • {filteredTemplates.length} template ditampilkan</p>
                            </div>
                            <div className="flex w-full gap-2 md:w-auto">
                                <button onClick={handleSaveAll} disabled={isSaving} className={`${btnBase} flex-1 bg-emerald-600 text-white hover:bg-emerald-700 md:flex-none`}>
                                    {isSaving ? <Spinner /> : <Icon name="save" />} {isSaving ? "Menyimpan…" : "Simpan Library"}
                                </button>
                                <button onClick={handleCreateTemplate} className={`${btnBase} flex-1 bg-slate-900 text-white hover:bg-slate-800 md:flex-none`}>
                                    <Icon name="plus" /> Buat Template
                                </button>
                            </div>
                        </div>

                        {/* Grid Card Template */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
                            {filteredTemplates.map(tpl => (
                                <TemplateCard
                                    key={tpl.id}
                                    tpl={tpl}
                                    locked={!!lockedTemplates[tpl.id]}
                                    onChange={handleTemplateChange}
                                    onImage={handleTemplateImage}
                                    onToggleLock={toggleLock}
                                    onDelete={handleDeleteTemplate}
                                    onPreview={setPreviewItem}
                                    onSend={handleSendTemplate}
                                />
                            ))}
                        </div>

                        {filteredTemplates.length === 0 && (
                            <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
                                <p className="text-sm font-semibold text-slate-600">Belum ada template</p>
                                <p className="mt-1 text-xs text-slate-400">Klik “Buat Template” untuk menambahkan template baru.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* TAB: RIWAYAT */}
            {activeTab === "history" && (
                <HistoryTab logs={broadcastLogs} onClear={handleClearLogs} />
            )}

            {/* Indikator ukuran data Firestore */}
            <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                    <div className="mb-1.5 flex items-center justify-between text-xs">
                        <span className="font-medium text-slate-500">Ukuran data tersimpan (perkiraan)</span>
                        <span className={`font-semibold tabular-nums ${docPercent >= 90 ? 'text-red-600' : docPercent >= 70 ? 'text-amber-600' : 'text-slate-600'}`}>
                            {formatBytes(docBytes)} / ~1 MB
                        </span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-full rounded-full transition-all ${docPercent >= 90 ? 'bg-red-500' : docPercent >= 70 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${docPercent}%` }} />
                    </div>
                </div>
                {docPercent >= 80 && (
                    <p className="text-xs font-medium text-amber-700 sm:max-w-xs sm:text-right">Mendekati batas dokumen Firestore. Hapus gambar/slot lama agar penyimpanan tidak gagal.</p>
                )}
            </div>

            {/* Bilah simpan melayang saat ada perubahan */}
            {isDirty && !isLoadingBc && (
                <div className="fixed bottom-4 left-1/2 z-[1500] w-[92%] max-w-lg -translate-x-1/2">
                    <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-700 bg-slate-900/95 px-4 py-3 text-white shadow-2xl backdrop-blur">
                        <div className="flex items-center gap-2.5 text-sm">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-amber-400" />
                            <span className="font-medium">{saveError ? "Gagal sinkron, coba lagi" : "Perubahan belum disimpan"}</span>
                        </div>
                        <button onClick={handleSaveAll} disabled={isSaving} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold transition hover:bg-blue-500 active:scale-95 disabled:opacity-60">
                            {isSaving ? <Spinner /> : <Icon name="save" />} {isSaving ? "Menyimpan…" : "Simpan sekarang"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Broadcast;